import { useMutation } from "@tanstack/react-query";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { useAccountStore } from "features/Account/store/useAccountStore";
import { baseApiClient } from "data/modules/api/api.client";
import { SUBSCRIPTIONS_APIS } from "features/MySubscriptions/constants/MySubscriptions.constants";
import { useCallback, useRef } from "react";
import { isRequestSuccessful } from "utils/Utils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { logError } from "utils/error";
import { getAccessTypeTenurePlansFromApiResponse } from "../utils/accessTypePlans.utils";

export const useOpenAccessTypePurchaseModal = (
  featureKey,
  { modalTitle, onSuccess } = {}
) => {
  const { notify } = useNotifications();
  const isOpeningRef = useRef(false);

  const accessTypePlans = useAccountStore((state) => state.accessTypePlans);
  const accessTypePlansFeatureKey = useAccountStore(
    (state) => state.accessTypePlansFeatureKey
  );
  const openAccessTypePlansModal = useAccountStore(
    (state) => state.openAccessTypePlansModal
  );

  const cachedPlans =
    accessTypePlansFeatureKey === featureKey ? accessTypePlans : [];

  const {
    mutateAsync: fetchAccessTypePlans,
    isPending: isOpeningAccessTypePlans,
  } = useMutation({
    mutationFn: async () => {
      const response = await baseApiClient.get(
        SUBSCRIPTIONS_APIS.SUBSCRIPTIONS_PLANS,
        { params: { feature_key: featureKey } }
      );
      return response;
    },
  });

  const openPlansModalWithPlans = useCallback(
    (plans, e) => {
      openAccessTypePlansModal({
        featureKey,
        plans,
        modalTitle,
      });
      if (onSuccess) {
        onSuccess(e);
      }
    },
    [featureKey, modalTitle, onSuccess, openAccessTypePlansModal]
  );

  const openAccessTypePurchaseModal = useCallback(
    async (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!featureKey) return;

      if (isOpeningRef.current || isOpeningAccessTypePlans) {
        return;
      }

      try {
        if (checkIsArrayEmpty(cachedPlans)) {
          isOpeningRef.current = true;
          const response = await fetchAccessTypePlans();

          if (isRequestSuccessful(response?.status)) {
            const tenurePlans =
              getAccessTypeTenurePlansFromApiResponse(response);

            if (!checkIsArrayEmpty(tenurePlans)) {
              openPlansModalWithPlans(tenurePlans, e);
              return;
            }
          }

          notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
          return;
        }

        openPlansModalWithPlans(cachedPlans, e);
      } catch (error) {
        logError({
          error,
          occuredAt:
            "src/features/MySubscriptions/hooks/useOpenAccessTypePurchaseModal.js",
          when: "openAccessTypePurchaseModal_fetch_subscription_plans_failed",
        });
        notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
      } finally {
        isOpeningRef.current = false;
      }
    },
    [
      cachedPlans,
      featureKey,
      fetchAccessTypePlans,
      isOpeningAccessTypePlans,
      notify,
      openPlansModalWithPlans,
    ]
  );

  return { openAccessTypePurchaseModal, isOpeningAccessTypePlans };
};
